/**
 * Small, pure numeric helpers shared by every motion primitive. No DOM, no state — these run in
 * rAF loops, so they allocate nothing and are trivially testable.
 */

export const clamp = (value: number, min = 0, max = 1): number =>
  value < min ? min : value > max ? max : value;

export const lerp = (from: number, to: number, t: number): number => from + (to - from) * t;

/** Linear 0‥1 position of `value` inside [`start`, `end`] (clamped). `end === start` yields 1 once reached. */
export const progress = (value: number, start: number, end: number): number =>
  end === start ? (value >= end ? 1 : 0) : clamp((value - start) / (end - start));

/**
 * Frame-rate independent exponential smoothing ("lerp with dt"): moves `current` towards `target`
 * so that the remaining distance halves every `halfLife` milliseconds, whatever the frame time.
 * (A fixed `lerp(a, b, .12)` runs twice as fast on a 120 Hz display.)
 */
export function damp(current: number, target: number, halfLifeMs: number, dtMs: number): number {
  if (halfLifeMs <= 0) return target;
  const k = 1 - 0.5 ** (dtMs / halfLifeMs);
  return current + (target - current) * k;
}

export const degrees = (radians: number): number => (radians * 180) / Math.PI;
export const radians = (deg: number): number => (deg * Math.PI) / 180;

/** `smoothstep` — cubic Hermite between two edges. */
export function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

export type Bezier = readonly [x1: number, y1: number, x2: number, y2: number];

/** The design-system curves as CSS does (`--ease-*` in tokens.css), for animations driven from JS. */
export const EASE = {
  reveal: [0.22, 1, 0.36, 1],
  expo: [0.16, 1, 0.3, 1],
  ui: [0.4, 0, 0.2, 1],
  decel: [0, 0, 0.2, 1],
  accel: [0.4, 0, 1, 1],
  soft: [0.45, 0, 0.06, 1],
} as const satisfies Record<string, Bezier>;

/**
 * A CSS-compatible cubic-bezier easing function (same curve the browser would use for
 * `cubic-bezier(x1, y1, x2, y2)`): Newton–Raphson with a bisection fallback.
 */
export function cubicBezier(curve: Bezier): (t: number) => number {
  const [x1, y1, x2, y2] = curve;
  if (x1 === y1 && x2 === y2) return (t) => clamp(t); // linear

  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;

  const sampleX = (t: number): number => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number): number => ((ay * t + by) * t + cy) * t;
  const slopeX = (t: number): number => (3 * ax * t + 2 * bx) * t + cx;

  const solveT = (x: number): number => {
    let t = x;
    for (let i = 0; i < 8; i += 1) {
      const error = sampleX(t) - x;
      if (Math.abs(error) < 1e-6) return t;
      const slope = slopeX(t);
      if (Math.abs(slope) < 1e-6) break;
      t -= error / slope;
    }
    let low = 0;
    let high = 1;
    t = x;
    while (low < high) {
      const value = sampleX(t);
      if (Math.abs(value - x) < 1e-6) return t;
      if (x > value) low = t;
      else high = t;
      t = (high - low) / 2 + low;
      if (high - low < 1e-7) break;
    }
    return t;
  };

  return (x) => (x <= 0 ? 0 : x >= 1 ? 1 : sampleY(solveT(x)));
}

export const easeReveal = cubicBezier(EASE.reveal);
export const easeExpo = cubicBezier(EASE.expo);
export const easeUi = cubicBezier(EASE.ui);
